import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of, throwError } from "rxjs";
import { flow } from "ramda";
import { catchError, delay, map, pluck, shareReplay } from "rxjs/operators";
import * as faker from "@faker-js/faker";
import {
  Category,
  CategoryWithMenu,
  Constants,
  CreateCategoryParam,
  CreateCustomisation,
  CreateMenuItemParam,
  CreateMenuParam,
  CreateModifier,
  CreatePromotionDTO,
  DriaryAttribute,
  FoodCustomisation,
  FoodItem,
  FoodItemDTO,
  FoodItemTemperature,
  FoodService,
  MenuCategoryDisplayType,
  MenuItemConfigParam,
  MenuProfile,
  MenuShallowProfile,
  MerchantCategory,
  Modifier,
  OrderConfigSetting,
  PaginatedPromotions,
  PriceBy,
  Promotion,
  PromotionType,
  RestaurantAddress,
  RestaurantProfile,
  RestaurantStatus,
  RestaurantStatuses,
  UpdatePromotionDTO,
  UpdateRestauranProfileParam,
} from "./food.service.base";
import { MerchantService } from "./merchant.service";
import { HttpRequestsService } from "../http-requests.service";
import { MERCHANT_ROUTES } from "src/app/constants/constants";
import { environment } from "src/environments/environment";
import { formDataTrait } from "src/app/utils/form-data";
import * as apiTranslator from "./food.service.api.translator";
import { CreateMenuDto, MenuDto } from "./food.service.api.dto";
import { DataService } from "../cache-layer.service";
import { HttpParams } from "@angular/common/http";
import { match } from "ts-pattern";
import { PaginationDto } from "./dto.interface";
import { OrderItem } from "src/app/pages/merchant/food-order/models/food-order.detail";
import { Restaurant } from "../restaurant-selection.service";

@Injectable({
  providedIn: "root",
})
export class ApiFoodService extends FoodService {
  private foodServiceHost: string = environment.FOOD_SERVICE;
  private mockDelay = 2000;
  customisations: FoodCustomisation[] = [
    {
      id: "1",
      name: "Extra Cheese",
      note: "Extra cheese on top of the pizza",
    },
  ];

  constructor(
    private merchantService: MerchantService,
    private http: HttpRequestsService,
    private cacheDataService: DataService,
  ) {
    super();
  }

  protected isFoodMerchant: Observable<boolean>;

  protected categoryLists: BehaviorSubject<CategoryWithMenu[] | null> =
    new BehaviorSubject(null);

  protected configurations: BehaviorSubject<MenuItemConfigParam | null> =
    new BehaviorSubject(null);

  checkIfFoodMerchant(reCheck = false) {
    if (!reCheck && this.isFoodMerchant) {
      return this.isFoodMerchant;
    }
    this.isFoodMerchant = this.merchantService.getProfile(true).pipe(
      map((userProfile) => {
        return userProfile.isSmartieFoods;
      }),
      shareReplay(1),
    );
    return this.isFoodMerchant;
  }

  async getRestaurantProfile(): Promise<RestaurantProfile | null> {
    const apiDtoRestaurant = await this.http.rawGet(
      this.foodServiceHost,
      MERCHANT_ROUTES.GET_DEFAULT_RESTAURANT,
    );
    return (
      apiDtoRestaurant &&
      apiTranslator.translateToRestaurantProfile(apiDtoRestaurant.data)
    );
  }

  async saveRestaurantProfile(
    updateData: UpdateRestauranProfileParam,
    logo: File,
    banner: File,
  ): Promise<void> {
    const data = flow(new FormData(), [
      formDataTrait.guardedSet("name", updateData.name),
      formDataTrait.guardedSet("contactNumber", updateData.phoneNumber),
      formDataTrait.guardedSet("timezone", updateData.timezone),
      formDataTrait.guardedSet("logo", logo),
      formDataTrait.guardedSet("background", banner),
      ...updateData.cuisines.map((cuisineSlug, index) => {
        return formDataTrait.guardedSet(
          `merchantCategorySlugs[${index}]`,
          cuisineSlug,
        );
      }),
    ]);
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_RESTAURANT_DEFAULT,
      data,
      false,
    );
  }

  async becomeFoodMerchant(
    restaurantProfile: UpdateRestauranProfileParam,
  ): Promise<void> {
    const data = flow(new FormData(), [
      // formDataTrait.guardedSet("email", restaurantProfile.email),
      formDataTrait.guardedSet("city", restaurantProfile.address.city),
      formDataTrait.guardedSet("country", restaurantProfile.address.country),
      formDataTrait.guardedSet("state", restaurantProfile.address.state),
      formDataTrait.guardedSet("street", restaurantProfile.address.address),
      formDataTrait.guardedSet("name", restaurantProfile.name),
      formDataTrait.guardedSet("phoneNumber", restaurantProfile.phoneNumber),
      formDataTrait.guardedSet("timezone", restaurantProfile.timezone),
      formDataTrait.guardedSet("logo", restaurantProfile.logo),
      formDataTrait.guardedSet(
        "latitude",
        restaurantProfile.address.lat?.toString(),
      ),
      formDataTrait.guardedSet(
        "longitude",
        restaurantProfile.address.long?.toString(),
      ),
      ...restaurantProfile.cuisines.map((cuisineSlug, index) => {
        return formDataTrait.guardedSet(
          `categoryCuisineSlugs[${index}]`,
          cuisineSlug,
        );
      }),
    ]);
    await this.http.rawPost(
      this.foodServiceHost,
      MERCHANT_ROUTES.BECOME_FOOD_MERCHANT,
      data,
      false,
      {},
      false,
    );
    this.checkIfFoodMerchant(true);
  }

  async createNewMenu(createDto: CreateMenuParam) {
    const menuDto: CreateMenuDto = apiTranslator.convertToMenuDto(createDto);

    try {
      // Make the HTTP POST request
      const response: MenuDto = await this.http
        .rawPost<{
          data: MenuDto;
        }>(
          this.foodServiceHost,
          MERCHANT_ROUTES.ADD_NEW_MENU,
          menuDto,
          false,
          {},
          false,
        )
        .then((res) => res.data);

      // Return the API response as MenuProfile
      return apiTranslator.mapMenuDtoToMenuProfile(response);
    } catch (error) {
      console.error("Error creating new menu:", error);
      throw error; // Re-throw the error after logging it
    }
  }

  getMenuDetail(id: string): Observable<MenuProfile> {
    // cache stale-while-validate
    return this.cacheDataService
      .getData(`menu-detail-${id}`, () =>
        this.http.rawGet<{ data: MenuProfile }>(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.GET_MENU}/${id}`,
        ),
      )
      .pipe(
        map((response) => {
          if (response) {
            return response.data;
          } else {
            throw new Error("No response received");
          }
        }),
        catchError((error) => {
          console.error("Error fetching menu detail:", error);
          return throwError(() => error);
        }),
      );
  }

  getMenuList(): Observable<Array<MenuShallowProfile>> {
    return from(
      this.http.rawGet<{ data: MenuDto[] } | MenuDto[]>(
        this.foodServiceHost,
        MERCHANT_ROUTES.GET_DEFAULT_MENU_LIST,
      ),
    ).pipe(
      map((response) => {
        const rawMenus = response;
        if (!Array.isArray(rawMenus)) {
          return [];
        }
        return rawMenus.map(apiTranslator.mapMenuDtoToMenuShallowProfile);
      }),
      catchError((error) => {
        console.error("Error fetching menu list:", error);
        return throwError(() => error);
      }),
    );
  }

  async updateMenu(menu: MenuProfile): Promise<MenuProfile> {
    try {
      const response: any = await this.http.rawPut(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.UPDATE_MENU}/${menu.id}`,
        menu,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating customisation:", error);
      throw error;
    }
  }

  getCategoryList(): Observable<CategoryWithMenu[]> {
    return this.cacheDataService
      .getData("category-list", () =>
        this.http.rawGet<{ data: CategoryWithMenu[] }>(
          this.foodServiceHost,
          MERCHANT_ROUTES.GET_MENU_CATEGORIES,
        ),
      )
      .pipe(map((res) => res.data))
      .pipe(
        catchError((error) => {
          console.error("Error getting categories:", error);
          return throwError(() => error);
        }),
      );
  }

  getItemDetail(id: string): Observable<FoodItem | null> {
    return this.cacheDataService
      .getData(`item-detail-${id}`, () =>
        this.http.rawGet(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.MENU_ITEM}/${id}`,
          false,
          {},
          {},
          false,
        ),
      )
      .pipe(map(apiTranslator.mapItemDtoToFoodItem));
  }

  getItemModifierGroups(id: string): Observable<FoodItem["modifierGroups"]> {
    return from(
      this.http.rawGet(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.MENU_ITEM}/${id}/modifier-groups`,
      ),
    )
      .pipe(pluck("data"))
      .pipe(
        map((modGroups: any[]) =>
          modGroups.map(apiTranslator.mapGMDtoToGroupMod),
        ),
      );
  }

  deleteItemModifierGroup(id: string, modId: string) {
    return from(
      this.http.rawDelete(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.MENU_ITEM}/${id}/modifier-groups/${modId}`,
      ),
    );
  }

  private menuItemParamsToFormData(
    menuItem: CreateMenuItemParam & { id?: string },
  ) {
    const formData = new FormData();
    // Append all form values to FormData
    Object.keys(menuItem).forEach((key) => {
      if (key === "profileImageUrl") {
        if (menuItem[key] instanceof File) {
          formData.append("itemImage", menuItem[key], menuItem[key].name);
        }
      } else if (key === "categoryIds") {
        // Ensure categoryIds is an array
        // Append each categoryId separately
        menuItem.categoryIds.forEach((id: string, index: number) => {
          formData.append(`categoryIds[${index}]`, id);
        });
      } else if (key === "dietaryAttributes") {
        menuItem.dietaryAttributes.forEach((attr: string, index: number) => {
          formData.append(`dietaryAttributes[${index}]`, attr);
        });
      } else if (key === "modifierGroups") {
        menuItem.modifierGroups.forEach((mG, index) => {
          formData.append(`modifierGroups[${index}]`, mG.id);
        });
      } else {
        formData.append(key, menuItem[key]);
      }
    });
    return formData;
  }

  updateMenuItem(
    menuItem: CreateMenuItemParam & { id: string },
  ): Observable<FoodItem> {
    const url = `${MERCHANT_ROUTES.MENU_ITEM}/${menuItem.id}`;
    const formData = this.menuItemParamsToFormData(menuItem);
    return from(
      this.http.rawPut<FoodItem>(
        this.foodServiceHost,
        url,
        formData,
        undefined,
        undefined,
        false,
      ),
    ).pipe(
      catchError((error) => {
        console.error("Error updating food item:", error);
        return throwError(() => error);
      }),
    );
  }

  deleteMenuItem(id: string): Observable<any> {
    const url = `${MERCHANT_ROUTES.MENU_ITEM}/${id}`;
    return from(this.http.rawDelete(this.foodServiceHost, url)).pipe(
      catchError((error) => {
        console.error("Error deleting food item:", error);
        return throwError(() => error);
      }),
    );
  }

  createMenuItem(menuItem: CreateMenuItemParam): Observable<FoodItem> {
    const formData = this.menuItemParamsToFormData(menuItem);
    return from(
      this.http.rawPost<FoodItem>(
        this.foodServiceHost,
        MERCHANT_ROUTES.MENU_ITEM,
        formData,
        undefined,
        undefined,
        false,
      ),
    ).pipe(
      catchError((error) => {
        console.error("Error creating food item:", error);
        return throwError(error);
      }),
    );
  }

  getConfiguration(): Observable<MenuItemConfigParam> {
    if (!this.configurations.getValue()) {
      this.configurations.next({
        foodItemTemperatures: Object.keys(FoodItemTemperature).map((k) => ({
          name: FoodItemTemperature[k],
          id: k,
        })),
        priceByOptions: Object.keys(PriceBy).map((k) => ({
          name: PriceBy[k],
          id: k,
        })),
        dietaryAttributes: Object.keys(DriaryAttribute).map((k) => ({
          name: DriaryAttribute[k],
          id: k,
        })),
      });
    }
    return this.configurations.asObservable();
  }
  getFoodItems(params: {
    page: number;
    search?: string;
    limit: number;
    categoryIds?: string[];
  }): Observable<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodItemDTO[];
  }> {
    let httpParams = new HttpParams()
      .set("page", params.page.toString())
      .set("limit", params.limit.toString());

    if ((params.categoryIds || []).length) {
      httpParams = httpParams.set("categoryIds", params.categoryIds.join(","));
    }

    if (params.search) {
      httpParams = httpParams.set("search", params.search);
    }

    const url = `${MERCHANT_ROUTES.MENU_ITEM}?${httpParams.toString()}`;

    return from(
      this.http.rawGet<{
        meta: {
          total: number;
          page: number;
          limit: number;
        };
        data: FoodItemDTO[];
      }>(this.foodServiceHost, url),
    ).pipe(
      map((response) => ({
        meta: response.meta,
        data: response.data,
      })),
      catchError((error) => {
        console.error("Error getting menu items:", error);
        throw error;
      }),
    );
  }

  addNewCategory(createDto: CreateCategoryParam): Observable<Category> {
    return from(
      this.http.rawPost<Category>(
        this.foodServiceHost,
        MERCHANT_ROUTES.CREATE_MENU_CATEGORY,
        createDto,
        false,
        {},
        false,
      ),
    );
  }
  updateCategory(category: Category): Observable<Category> {
    try {
      return from(
        this.http.rawPut(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.UPDATE_MENU_CATEGORY}/${category.id}`,
          category,
        ),
      ).pipe(
        map((response) => response),
        catchError((error) => {
          console.error("Error updating category:", error);
          throw error;
        }),
      );
    } catch (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  }

  getItemsOnCustomisation(params: {
    modifierGroupId: string;
    page: number;
    limit: number;
    search: string;
  }): Observable<PaginationDto<OrderItem & { isAssigned: boolean }>> {
    let httpParams = new HttpParams()
      .set("page", params.page.toString())
      .set("limit", params.limit.toString());

    if (params.search) {
      httpParams = httpParams.set("search", params.search);
    }

    return from(
      this.http.rawGet(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.GET_CUSTOMISATIONS}/${params.modifierGroupId}/food-items?${httpParams.toString()}`,
      ),
    );
  }

  getCategoryDetail(id: string): Observable<CategoryWithMenu> {
    try {
      return from(
        this.http.rawGet(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.UPDATE_MENU_CATEGORY}/${id}`,
        ),
      ).pipe(
        map((response) => response),
        catchError((error) => {
          console.error("Error getting category details:", error);
          throw error;
        }),
      );
    } catch (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  }

  async getCustomisations(params: {
    page: number;
    search: string;
    limit: number;
  }): Promise<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodCustomisation[];
  }> {
    try {
      // Construct the URL with parameters
      let url = `${MERCHANT_ROUTES.GET_CUSTOMISATIONS}?page=${params.page}&limit=${params.limit}`;

      // Add search parameter only if it's not empty
      if (params.search && params.search.trim() !== "") {
        url += `&search=${encodeURIComponent(params.search.trim())}`;
      }

      const response: any = await this.http.rawGet<MenuDto>(
        this.foodServiceHost,
        url,
      );

      return {
        meta: response.meta,
        data: (response.data || []).map((raw) => ({
          ...raw,
          maxQuantityPerOption: raw.maxQuantityOfEachModifier,
        })),
      };
    } catch (error) {
      console.error("Error fetching customisations:", error);
      throw error;
    }
  }

  async createCustomisation(
    createCustomisation: CreateCustomisation,
  ): Promise<{ data: FoodCustomisation }> {
    try {
      const response: any = await this.http.rawPost<FoodCustomisation>(
        this.foodServiceHost,
        MERCHANT_ROUTES.CREATE_CUSTOMISATION,
        createCustomisation,
      );
      return {
        data: response.data,
      };
    } catch (error) {
      console.error("Error creating customisation:", error);
      throw error;
    }
  }

  async updateCustomisation(
    updatedCustomisation: CreateCustomisation & { id: string },
  ): Promise<{ data: FoodCustomisation }> {
    try {
      const response: any = await this.http.rawPut<FoodCustomisation>(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${updatedCustomisation.id}`,
        updatedCustomisation,
      );
      return {
        data: response.data,
      };
    } catch (error) {
      console.error("Error creating customisation:", error);
      throw error;
    }
  }

  async addModifierToModifierGroup(
    modifierGroupId: string,
    createModifier: CreateModifier,
  ): Promise<Modifier> {
    try {
      const response: Modifier = await this.http.rawPost<Modifier>(
        this.foodServiceHost,
        MERCHANT_ROUTES.CREATE_CUSTOMISATION +
          "/" +
          modifierGroupId +
          "/modifier",
        createModifier,
      );
      return response;
    } catch (error) {
      console.error("Error creating modifier:", error);
      throw error;
    }
  }

  async deleteCustomisation(
    modifierGroupId: string,
  ): Promise<{ success: boolean }> {
    try {
      const response: any = await this.http.delete(
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}`,
      );
      return response;
    } catch (error) {
      console.error("Error deleting modifier group:", error);
      throw error;
    }
  }

  async deleteModifier(
    modifierGroupId: string,
    modifierId: string,
  ): Promise<{ success: boolean }> {
    try {
      const response: any = await this.http.delete(
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/modifier/${modifierId}`,
      );
      return response;
    } catch (error) {
      console.error("Error deleting modifier:", error);
      throw error;
    }
  }

  async updateModifierDisplayOrderIndex(
    modifierGroupId: string,
    modifiers: { id: string; orderIndex: number }[],
  ): Promise<FoodCustomisation> {
    try {
      const response: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/modifier/display-order`,
        { modifiers: modifiers },
      );
      return response;
    } catch (error) {
      console.error("Error updating modifier order:", error);
      throw error;
    }
  }

  async updateModifierGroupRules(
    modifierGroupId: string,
    rules: {
      minSelection: number;
      maxSelection: number;
      multipleSelections: boolean;
      maxQuantityOfEachModifier?: number;
      isRequired?: boolean;
    },
  ): Promise<FoodCustomisation> {
    try {
      const response: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/rules`,
        rules,
        {},
        false,
        false,
      );
      return response;
    } catch (error) {
      console.error("Error updating modifier rules:", error);
      throw error;
    }
  }

  async updateItemsInCustomisation(
    modifierGroupId: string,
    addedItemIds: string[],
    removedItemIds: string[],
  ): Promise<FoodCustomisation> {
    try {
      const res: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/update-items`,
        { addedItemIds, removedItemIds },
      );
      return res;
    } catch (error) {
      console.error("Error adding customisation to items:", error);
      throw error;
    }
  }

  async updateItemCategoryOrder(
    categoryId: string,
    ids: string[],
  ): Promise<void> {
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_CAT_ITEMS_ORDER.replace(":categoryId", categoryId),
      {
        itemOrders: ids.map((id, index) => ({
          itemId: id,
          order: index,
        })),
      },
    );
  }

  async updateCategoryOrder(menuId: string, catIds: string[]): Promise<void> {
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_CAT_ORDER.replace(":menuId", menuId),
      {
        orders: catIds.map((id, index) => ({
          catId: id,
          order: index,
        })),
      },
    );
  }

  async updateCategoryDisplayType(
    menuId: string,
    catId: string,
    displayType: MenuCategoryDisplayType,
  ): Promise<void> {
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_CAT_DISPLAY_TYPE.replace(
        ":menuId",
        menuId,
      ).replace(":catId", catId),
      {
        displayType,
      },
    );
  }

  updateRestaurantStatus(
    status: RestaurantStatus,
  ): Observable<RestaurantStatus> {
    const utilDate = status.remainingTime
      ? new Date(Date.now() + status.remainingTime * 1000)
      : null;
    const updateDto = match(status.type)
      .with(RestaurantStatuses.OPEN, () => ({
        isOpen: true,
      }))
      .with(RestaurantStatuses.CLOSE, () => ({
        isOpen: false,
      }))
      .with(RestaurantStatuses.BUSY, () => ({
        isBusy: true,
        busyOrderDelay: status.addingTime,
      }))
      .with(RestaurantStatuses.PAUSED, () => ({
        isPaused: true,
        pauseEndTime: utilDate,
        pauseUntilNextOpenTime: status.pauseUntilNextOpenTime,
      }))
      .exhaustive();
    return from(
      this.http.rawPut(
        this.foodServiceHost,
        MERCHANT_ROUTES.RESTAURANT_STATUS,
        updateDto,
      ),
    ).pipe(map((response) => this.transformRestaurantStatus(response.data)));
  }

  getRestaurantStatus(): Observable<RestaurantStatus> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.RESTAURANT_STATUS),
    ).pipe(map((response) => this.transformRestaurantStatus(response.data)));
  }

  private transformRestaurantStatus(data: any): RestaurantStatus {
    const now = new Date();

    if (data.isPaused && data.pauseEndTime) {
      const remainingTime = Math.max(
        0,
        (new Date(data.pauseEndTime).getTime() - now.getTime()) / 1000,
      );
      return {
        type: RestaurantStatuses.PAUSED,
        remainingTime: Math.round(remainingTime),
        addingTime: null,
        pauseUntilNextOpenTime: null,
      };
    }

    if (data.isBusy) {
      const remainingTime = Math.max(
        0,
        (new Date(data.busyEndTime).getTime() - now.getTime()) / 1000,
      );
      return {
        type: RestaurantStatuses.BUSY,
        addingTime: data.busyOrderDelay,
        remainingTime,
        pauseUntilNextOpenTime: null,
      };
    }

    if (!data.isOpen) {
      return {
        type: RestaurantStatuses.CLOSE,
        remainingTime: data.closeEndTime,
        addingTime: null,
        pauseUntilNextOpenTime: null,
      };
    }

    return {
      type: RestaurantStatuses.OPEN,
      remainingTime: null,
      addingTime: null,
      pauseUntilNextOpenTime: null,
    };
  }

  getConstants(): Observable<Constants> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.CONSTANT),
    ).pipe(
      map((response) => ({
        orderStatuses: (response.data.orderStatuses || []).map((status) => ({
          id: status,
          name: this.formatStatusName(status),
        })),
        timezones: (response.data.timezones || []).map((tz) => ({
          id: tz,
          name: tz,
        })),
      })),
    );
  }

  getAllMerchantCategory(): Observable<MerchantCategory[]> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.MERCHANT_CATEGORY),
    ).pipe(
      map((response) =>
        (response.data || []).map((catRes) => ({
          name: catRes.name,
          slug: catRes.slug,
        })),
      ),
    );
  }

  private formatStatusName(status: string): string {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  getPromotions(
    page: number = 1,
    limit: number = 10,
    filter: {
      types?: PromotionType[];
      isActive?: boolean;
    } = {},
  ): Observable<PaginatedPromotions> {
    const params = [`page=${page}&limit=${limit}`];
    if (filter.types && filter.types.length > 0) {
      params.push(`types=${filter.types.join(",")}`);
    }
    if (filter.isActive != null) {
      params.push(`isActive=${filter.isActive}`);
    }
    return from(
      this.http.rawGet<PaginatedPromotions>(
        this.foodServiceHost,
        `foods/promotions?${params.join("&")}`,
      ),
    );
  }
  createPromotion(promotionData: CreatePromotionDTO): Observable<Promotion> {
    const formattedData = {
      ...promotionData,
      startDate: new Date(promotionData.startDate).toISOString(),
      endDate: new Date(promotionData.endDate).toISOString(),
    };
    return from(
      this.http.rawPost<Promotion>(
        this.foodServiceHost,
        "foods/promotions",
        formattedData,
        false,
        undefined,
        false,
      ),
    );
  }

  updatePromotion(
    promotionId: string,
    updateData: UpdatePromotionDTO,
  ): Observable<Promotion> {
    const formattedData = {
      ...updateData,
      ...(updateData.startDate && {
        startDate: new Date(updateData.startDate).toISOString(),
      }),
      ...(updateData.endDate && {
        endDate: new Date(updateData.endDate).toISOString(),
      }),
    };

    return from(
      this.http.rawPatch<Promotion>(
        this.foodServiceHost,
        `foods/promotions/${promotionId}`,
        formattedData,
        false,
        undefined,
        false,
      ),
    );
  }

  deletePromotion(promotionId: string): Observable<void> {
    return from(
      this.http.rawDelete(
        this.foodServiceHost,
        `foods/promotions/${promotionId}`,
      ) as Promise<void>,
    );
  }

  getPromotionById(promotionId: string): Observable<Promotion> {
    return from(
      this.http.rawGet<any>(
        this.foodServiceHost,
        `foods/promotions/${promotionId}`,
      ),
    ).pipe(
      map((promotionDetail) => ({
        ...promotionDetail,
        availableDays: (promotionDetail.availableDays || []).map((day) =>
          parseInt(day),
        ),
        startDate: new Date(promotionDetail.startDate),
        endDate: new Date(promotionDetail.endDate),
      })),
    );
  }

  getPromotionPerformanceById(
    promotionId: string,
    query: { startDate?: Date; endDate?: Date },
  ): Observable<any> {
    let httpParams = new HttpParams();

    if (query.startDate) {
      httpParams = httpParams.set("startDate", query.startDate.toISOString());
    }
    if (query.endDate) {
      httpParams = httpParams.set("endDate", query.endDate.toISOString());
    }
    return from(
      this.http.rawGet<any>(
        this.foodServiceHost,
        `foods/promotions/${promotionId}/performance?${httpParams.toString()}`,
      ),
    ).pipe(
      map((promotionDetail) => ({
        ...promotionDetail,
        dailySales: (promotionDetail.dailySales || []).map((sale) => ({
          ...sale,
          date: new Date(sale.date),
        })),
      })),
    );
  }

  // getPromotionPerformanceById(
  //   promotionId: string,
  //   query,
  // ): Observable<Promotion> {
  //   return from(
  //     this.http.rawGet<any>(
  //       this.foodServiceHost,
  //       `foods/promotions/${promotionId}`,
  //     ),
  //   ).pipe(
  //     map((promotionDetail) => ({
  //       ...promotionDetail,
  //       startDate: new Date(promotionDetail.startDate),
  //       endDate: new Date(promotionDetail.endDate),
  //     })),
  //   );
  // }

  // updatePromotionStatus(
  //   promotionId: string,
  //   isActive: boolean,
  // ): Observable<void> {
  //   return from(
  //     this.http.rawPatch(
  //       this.foodServiceHost,
  //       `foods/promotions/${promotionId}/status`,
  //       { isActive },
  //     ) as Promise<void>,
  //   );
  // }

  // claimPromotion(promotionId: string): Observable<any> {
  //   return from(
  //     this.http.rawPost(
  //       this.foodServiceHost,
  //       `foods/promotions/${promotionId}/claim`,
  //       {},
  //     ),
  //   );
  // }

  updatePromotionStatus(
    promotionId: string,
    isActive: boolean,
  ): Observable<void> {
    return of(void 0).pipe(delay(this.mockDelay));
  }

  claimPromotion(promotionId: string): Observable<any> {
    const claimResult = {
      id: faker.faker.string.uuid(),
      promotionId,
      claimedAt: new Date().toISOString(),
      validUntil: faker.faker.date.future().toISOString(),
      status: "CLAIMED",
    };

    return of(claimResult).pipe(delay(this.mockDelay));
  }

  addAddress(address: Omit<RestaurantAddress, "id">): Observable<void> {
    const url = `foods/restaurant-addresses`;
    return from(
      this.http.rawPost(this.foodServiceHost, url, {
        name: address.name,
        city: address.city,
        state: address.state,
        country: address.country,
        latitude: `${address.lat}`,
        longitude: `${address.long}`,
      }) as Promise<void>,
    );
  }

  private dtoToAddress(addressDto: any): RestaurantAddress {
    return {
      id: addressDto.id,
      name: addressDto.name,
      city: addressDto.city,
      state: addressDto.state,
      country: addressDto.country,
      lat: addressDto.latitude,
      long: addressDto.longitude,
      address: addressDto.address,
    };
  }

  updateAddress(address: RestaurantAddress): Observable<void> {
    const url = `foods/restaurant-addresses/${address.id}`;
    return from(
      this.http.rawPatch(this.foodServiceHost, url, {
        name: address.name,
        city: address.city,
        state: address.state,
        country: address.country,
        latitude: `${address.lat}`,
        longitude: `${address.long}`,
      }) as Promise<void>,
    );
  }
  removeAddress(addressId: string): Observable<void> {
    const url = `foods/restaurant-addresses/${addressId}`;
    return from(
      this.http.rawDelete(this.foodServiceHost, url) as Promise<void>,
    );
  }

  getAddresses(): Observable<RestaurantAddress[]> {
    const url = `foods/restaurant-addresses`;
    return from(this.http.rawGet(this.foodServiceHost, url)).pipe(
      map((dto) => (dto?.data || []).map(this.dtoToAddress)),
    );
  }

  selectAddressAsDefault(id: string): Observable<void> {
    const url = `foods/restaurant-addresses-default`;
    return from(
      this.http.rawPut(this.foodServiceHost, url, {
        id,
      }) as Promise<void>,
    );
  }

  getNotifUnreadCount(): Observable<number> {
    const url = `foods/restaurant/notifications/unread-count`;
    return from(this.http.rawGet(this.foodServiceHost, url)).pipe(
      map((data) => data.count),
    );
  }

  saveRestaurantOrderSetting(
    orderSetting: OrderConfigSetting,
  ): Observable<void> {
    const url = "foods/restaurant-order-config";
    return from(
      this.http.rawPatch(this.foodServiceHost, url, {
        deliveryPriceIncreasePercentage:
          orderSetting.deliveryPriceIncreasePercentage,
      }) as Promise<void>,
    );
  }

  bulkEditMenuItems(
    items: {
      id: string;
      name?: string;
      price?: string;
    }[],
  ): Observable<any> {
    const url = "foods/menu-items/bulk-edit";
    return from(this.http.rawPut(this.foodServiceHost, url, { items }));
  }

  getRestaurants(): Observable<Restaurant[]> {
    const url = "foods/restaurant-list";
    return from(this.http.rawGet(this.foodServiceHost, url)).pipe(
      map((response) => response.data || []),
    );
  }
}
